import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  DateField,
  Modal,
  ModalActions,
  Page,
  PageHeader,
  Pagination,
  Select,
  Table,
  TextArea,
  TimeField,
  type Action,
  type Column,
} from '@evogard/ui';
import { Chart } from '@evogard/ui/chart';
import { businessDate, formatDate, instantFromHouseLocal } from '@evogard/domain';
import type { BreakDepartmentRow, BreakRecord } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  AdjustmentForm,
  ReviewForm,
  STATUS_LABEL,
  STATUS_TONE,
  clockOf,
  openFile,
  useBreakDepartments,
  useBreakHistory,
  useBreakRecords,
  useRefreshBreaks,
} from './breaks.js';

const PERIODS = [7, 14, 30, 90] as const;

// @valor
const ADJUSTMENT_LABEL = {
  pendente: 'Ajuste pendente',
  aprovado: 'Ajustado',
  reprovado: 'Ajuste reprovado',
} as const;

/**
 * A relação dos intervalos — `/admin/intervalos/relacao` do legado.
 *
 * Três alcances, e quem decide é o servidor (`scope` na resposta): quem tem
 * `breaks.records:ver` vê a base inteira; o líder de departamento vê os
 * próprios e a equipe dos setores que lidera; o resto vê os próprios ("Meus
 * intervalos"). Pedir ajuste é de qualquer um sobre o próprio intervalo
 * encerrado; o líder revisa os da equipe; ajustar direto e registrar à mão
 * pedem as permissões do módulo.
 */
export function RecordsScreen() {
  const { data: context } = useSessionContext();
  const me = context?.user.id;
  const canAdjust = can(context, 'breaks.records', 'ajustar');
  const canReview = can(context, 'breaks.records', 'revisar');
  // o registro manual escolhe a pessoa na lista da configuração
  const canManual = canAdjust && can(context, 'breaks.settings', 'editar');

  const mayLead = can(context, 'breaks.records', 'liderar');

  const [days, setDays] = useState<number>(7);
  /** `''` é a base inteira; `'0'` é "sem departamento". */
  const [department, setDepartment] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [manual, setManual] = useState(false);

  const filter = { days, departmentId: department === '' ? undefined : Number(department) };
  const records = useBreakRecords(filter, page, perPage);
  const history = useBreakHistory(filter);
  const scope = records.data?.scope ?? 'proprio';
  /* departamento é recorte de quem vê mais que a si: a base, ou a equipe */
  const byDepartment = scope !== 'proprio';
  const departments = useBreakDepartments(days, byDepartment);
  const departmentName = departments.data?.find(
    (d) => String(d.departmentId ?? 0) === department,
  )?.name;

  const columns: Column<BreakRecord>[] = [
    ...(byDepartment
      ? [{ title: 'Pessoa', cell: (r: BreakRecord) => r.userName, alignment: 'inicio' as const }]
      : []),
    { title: 'Dia', cell: (r) => formatDate(r.day) },
    { title: 'Horário', cell: (r) => r.slot ?? 'Livre' },
    { title: 'Início', cell: (r) => clockOf(r.startedAt) },
    { title: 'Fim', cell: (r) => (r.endedAt ? clockOf(r.endedAt) : '—') },
    {
      title: 'Duração',
      cell: (r) => (r.durationMinutes === null ? '—' : `${r.durationMinutes} min`),
      alignment: 'fim',
    },
    {
      title: 'Situação',
      cell: (r) => <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>,
    },
    {
      title: 'Ajuste',
      cell: (r) =>
        r.manualReason ? (
          <Badge tone="informacao">Registro manual</Badge>
        ) : r.adjustment ? (
          <Badge
            tone={
              r.adjustment.status === 'pendente'
                ? 'alerta'
                : r.adjustment.status === 'aprovado'
                  ? 'sucesso'
                  : 'neutro'
            }
          >
            {ADJUSTMENT_LABEL[r.adjustment.status]}
          </Badge>
        ) : (
          '—'
        ),
    },
  ];

  const actions = (r: BreakRecord): Action[] => {
    const list: Action[] = [];
    const pending = r.adjustment?.status === 'pendente';
    if (r.userId === me) {
      list.push({
        label: 'Pedir ajuste',
        icon: 'historico',
        disabled: pending
          ? 'Já existe uma solicitação esperando revisão.'
          : r.endedAt === null
            ? 'Encerre o intervalo antes de pedir ajuste.'
            : false,
        form: (close) => <AdjustmentForm record={r} mode="pedir" onDone={close} />,
      });
    }
    if (canAdjust) {
      list.push({
        label: 'Ajustar horário',
        icon: 'relogio',
        form: (close) => <AdjustmentForm record={r} mode="ajustar" onDone={close} />,
      });
    }
    /* o líder revisa os da equipe, e não os próprios — a API confere de novo */
    if (pending && (canReview || (mayLead && scope === 'lider' && r.userId !== me))) {
      list.push({
        label: 'Revisar solicitação',
        icon: 'confirmado',
        form: (close) => <ReviewForm record={r} onDone={close} />,
      });
    }
    for (const file of r.adjustment?.files ?? []) {
      list.push({ label: `Ver comprovante — ${file.name}`, icon: 'clipe', run: () => void openFile(file.id) });
    }
    return list;
  };

  return (
    <Page measure="cheia">
      <PageHeader
        title={
          scope === 'base'
            ? 'Relação dos intervalos'
            : scope === 'lider'
              ? 'Intervalos da equipe'
              : 'Meus intervalos'
        }
        actions={
          canManual && (
            <Button variant="contorno" icon="mais" onClick={() => setManual(true)}>
              Registrar intervalo
            </Button>
          )
        }
      >
        {scope === 'base'
          ? 'Todos os intervalos da base no período, com a situação de cada um.'
          : scope === 'lider'
            ? 'Os seus intervalos e os da equipe dos departamentos que você lidera.'
            : 'Os seus intervalos no período. Para corrigir um horário, peça ajuste com comprovante.'}
      </PageHeader>

      <div className="flex flex-wrap gap-3">
        <div className="w-60">
          <Select
            label="Período"
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setPage(1);
            }}
          >
            {PERIODS.map((d) => (
              <option key={d} value={d}>
                Últimos {d} dias
              </option>
            ))}
          </Select>
        </div>
        {byDepartment && (
          <div className="w-72">
            <Select
              label="Departamento"
              value={department}
              onChange={(e) => {
                setDepartment(e.target.value);
                setPage(1);
              }}
            >
              <option value="">
                {scope === 'lider' ? 'Toda a equipe' : 'Todos os departamentos'}
              </option>
              {(departments.data ?? []).map((d) => (
                <option key={d.departmentId ?? 0} value={d.departmentId ?? 0}>
                  {d.name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <Chart
        kind="barra"
        stacked
        title="Intervalos por dia"
        descricao="Pontual: encerrou até 1 minuto depois do fim previsto."
        label={`Intervalos por dia nos últimos ${days} dias${departmentName ? ` — ${departmentName}` : ''}`}
        data={history.data ?? []}
        loading={history.isLoading}
        error={history.error}
        empty="Nenhum intervalo no período."
        category={(d) => formatDate(d.day).slice(0, 5)}
        series={[
          // a cor é o estado: estourado é o vermelho de erro
          { key: 'punctual', label: 'Pontuais', value: (d) => d.punctual, color: 'fase-azul' },
          { key: 'overrun', label: 'Estourados', value: (d) => d.overrun, color: 'fase-vermelho' },
          { key: 'ongoing', label: 'Em andamento', value: (d) => d.ongoing, color: 'fase-laranja' },
        ]}
        height={220}
      />

      {records.isError && <Alert tone="erro">{records.error.message}</Alert>}
      {byDepartment && (
        <DepartmentsCard
          rows={departments.data ?? []}
          loading={departments.isLoading}
          error={departments.error}
          days={days}
          onPick={(id) => {
            setDepartment(String(id ?? 0));
            setPage(1);
          }}
        />
      )}

      <Card
        title={departmentName ? `Intervalos — ${departmentName}` : 'Intervalos'}
        descricao={
          records.data
            ? `${records.data.total} intervalo(s) no período.`
            : 'Carregando…'
        }
        bleed
      >
        <Table
          paginate={false}
          label="Intervalos"
          columns={columns}
          items={records.data?.items ?? []}
          rowKey={(r) => String(r.id)}
          rowTitle={(r) => `Intervalo de ${r.userName} em ${formatDate(r.day)}`}
          rowActions={actions}
          loading={records.isLoading}
          skeletonRows={perPage}
          empty="Nenhum intervalo no período."
        />
        {records.data && (
          <Pagination
            page={records.data.page}
            perPage={records.data.perPage}
            total={records.data.total}
            onChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
            label="intervalos"
            className="border-t border-borda-suave px-5 py-4"
          />
        )}
      </Card>

      {canManual && <ManualModal open={manual} onOpenChange={setManual} />}
    </Page>
  );
}

/**
 * O período por departamento. Quem está em dois departamentos conta nos dois,
 * então a soma das linhas pode passar do total da base — a descrição diz isso.
 * A ação da linha aplica o departamento como filtro da tela.
 */
function DepartmentsCard({
  rows,
  loading,
  error,
  days,
  onPick,
}: {
  rows: BreakDepartmentRow[];
  loading: boolean;
  error: Error | null;
  days: number;
  onPick: (departmentId: number | null) => void;
}) {
  const columns: Column<BreakDepartmentRow>[] = [
    { title: 'Departamento', cell: (d) => d.name, alignment: 'inicio' },
    { title: 'Pessoas', cell: (d) => d.people, alignment: 'fim' },
    { title: 'Intervalos', cell: (d) => d.total, alignment: 'fim' },
    { title: 'Pontuais', cell: (d) => d.punctual, alignment: 'fim' },
    { title: 'Estourados', cell: (d) => d.overrun, alignment: 'fim' },
    {
      title: '% estourado',
      cell: (d) => (d.total === 0 ? '—' : `${Math.round((d.overrun / d.total) * 100)}%`),
      alignment: 'fim',
    },
    {
      title: 'Duração média',
      cell: (d) => (d.averageMinutes === null ? '—' : `${d.averageMinutes} min`),
      alignment: 'fim',
    },
  ];
  return (
    <Card
      title="Por departamento"
      descricao={`Os últimos ${days} dias. Quem está em dois departamentos conta nos dois.`}
      bleed
    >
      {error && <Alert tone="erro">{error.message}</Alert>}
      <Table
        label="Intervalos por departamento"
        columns={columns}
        items={rows}
        rowKey={(d) => String(d.departmentId ?? 0)}
        rowTitle={(d) => d.name}
        rowActions={(d) => [
          { label: 'Ver só este departamento', icon: 'filtro', run: () => onPick(d.departmentId) },
        ]}
        loading={loading}
        empty="Nenhum departamento cadastrado nesta base."
      />
    </Card>
  );
}

/** Registrar um intervalo já encerrado para outra pessoa — ocupa o dia dela. */
function ManualModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const refresh = useRefreshBreaks();
  const people = useQuery({
    queryKey: ['intervalo', 'configuracao', 'pessoas'],
    enabled: open,
    queryFn: async () =>
      ok(await api.breaks.settings(), 200, 'Não foi possível carregar as pessoas.').people,
  });
  const [userId, setUserId] = useState('');
  const [day, setDay] = useState(() => businessDate(new Date()));
  const [start, setStart] = useState('15:00');
  const [end, setEnd] = useState('15:15');
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: async () => {
      const startedAt = instantFromHouseLocal(`${day}T${start}`);
      const endedAt = instantFromHouseLocal(`${day}T${end}`);
      if (!userId) throw new Error('Escolha a pessoa.');
      if (!startedAt || !endedAt) throw new Error('Informe os dois horários.');
      return ok(
        await api.breaks.manual({
          body: {
            userId,
            day,
            startedAt: startedAt.toISOString(),
            endedAt: endedAt.toISOString(),
            reason,
          },
        }),
        201,
        'Não foi possível registrar.',
      );
    },
    onSuccess: () => {
      refresh();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Registrar intervalo"
      descricao="Para quem esteve em intervalo e não ativou. Ocupa o dia: a pessoa não ativa outro."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Select label="Pessoa" required value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Escolha…</option>
          {(people.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <DateField label="Dia" required layer="modal" value={day} onChange={setDay} />
        <div className="grid grid-cols-2 gap-3">
          <TimeField label="Início" required layer="modal" value={start} onChange={setStart} />
          <TimeField label="Fim" required layer="modal" value={end} onChange={setEnd} />
        </div>
        <TextArea
          label="Motivo"
          required
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {save.isError && <Alert tone="erro">{save.error.message}</Alert>}
        <ModalActions>
          <Button type="submit" loading={save.isPending}>
            Registrar
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
